import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MoreHorizontal, PenLine, Trash2 } from 'lucide-react';
import Input from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { IClientCompany } from '../../types/types';
import { FileUpload } from '../../components/InputImage';
import useClientCompanies from '../../stores/useClientCompanies';
import Skeleton from '../../components/ui/skeleton';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';

interface CompanyCardProps {
  company: IClientCompany;
  onEdit: (company: IClientCompany) => void;
  onDelete: (id: number) => void;
}

function CompanyCard({ company, onEdit, onDelete }: CompanyCardProps) {
  return (
    <div className="border border-border hover-shadow bg-white rounded-lg px-4 py-3 text-sm items-center justify-center">
      <div className="flex items-center justify-between gap-2">
        <span
          className="cursor-pointer font-medium whitespace-nowrap overflow-hidden text-ellipsis"
          title={company.name_prt}
        >
          {company.name_prt}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="cursor-pointer" onClick={() => onEdit(company)}>
              <PenLine className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onClick={() => onDelete(company.partnercom_id)}>
              <Trash2 className="mr-2 h-4 w-4 text-red-500" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <img
        src={company.logo_prt || '/assets/no-data-image.svg'}
        alt={company.name_prt}
        className="w-full h-32 object-contain rounded-md mt-2"
      />
    </div>
  );
}

export default function ClientCompany() {
  const {
    clients,
    removeLoading,
    fetchLoading,
    hasFetched,
    saveLoading,
    fetchClients,
    addClient,
    editClient,
    removeClient,
  } = useClientCompanies();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [currentImage, setCurrentImage] = useState<string>('/assets/no-data-image.svg');
  const [selectedData, setSelectedData] = useState<{ id: number; name: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileUploadKey, setFileUploadKey] = useState(0);

  const { register, handleSubmit, setValue, reset } = useForm<IClientCompany>({
    defaultValues: { name_prt: '', logo_prt: '' },
  });

  useEffect(() => {
    if (!hasFetched) {
      fetchClients();
    }
  }, [fetchClients, hasFetched]);

  const onSubmit = async (data: IClientCompany) => {
    if (editingId) {
      await editClient(editingId, data);
      setEditingId(null);
    } else {
      await addClient(data);
    }
    reset();
    setCurrentImage('/assets/no-data-image.svg');
    setFileUploadKey((prevKey) => prevKey + 1);
  };

  const handleEdit = (company: IClientCompany) => {
    setValue('name_prt', company.name_prt);
    setValue('logo_prt', company.logo_prt);
    setCurrentImage(company.logo_prt || '/assets/no-data-image.svg');
    setEditingId(company.partnercom_id);
  };

  const handleCancel = () => {
    setEditingId(null);
    reset();
    setCurrentImage('/assets/no-data-image.svg');
  };

  const handleDeleteClick = (id: number, name: string) => {
    setSelectedData({ id, name });
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedData) {
      await removeClient(selectedData.id);
      setIsModalOpen(false);
      setSelectedData(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <h4 className="font-medium text-md">{editingId ? 'Edit Client' : 'Add New Client'}</h4>
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="flex lg:flex-row flex-col gap-2 lg:gap-4">
          <div className="flex flex-col text-sm w-full mt-2 gap-2">
            <p className="flex gap-1">
              Name <span className="text-red-600">*</span>
            </p>
            <Input {...register('name_prt')} type="text" placeholder="Masukkan nama klien..." />
          </div>
          <div className="flex flex-col text-sm w-full mt-2 gap-2">
            <p className="flex gap-1">
              Logo <span className="text-red-600">*</span>
            </p>
            <FileUpload
              key={fileUploadKey}
              setValue={setValue}
              register={register}
              imageFieldName="logo_prt"
              defaultImage={currentImage}
            />
          </div>
        </section>
        <div className="flex gap-2">
          {editingId && (
            <Button type="button" onClick={handleCancel} variant="secondary" className="mt-2">
              Batal
            </Button>
          )}
          <Button className="mt-2" type="submit" disabled={saveLoading}>
            {saveLoading ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </div>
      </form>
      <div className="w-full h-[1px] bg-border" />
      {fetchLoading ? (
        <section className="grid grid-cols-[repeat(auto-fill,_minmax(150px,_1fr))] gap-4 w-full">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </section>
      ) : (
        <div>
          {clients?.length === 0 ? (
            <div className="w-full h-48 flex justify-center items-center">
              <p className="px-3 py-2 text-neutral-300">No data</p>
            </div>
          ) : (
            <section className="grid grid-cols-[repeat(auto-fill,_minmax(150px,_1fr))] gap-4 w-full">
              {clients?.map((company) => (
                <CompanyCard
                  key={company.partnercom_id}
                  company={company}
                  onEdit={handleEdit}
                  onDelete={() => handleDeleteClick(company.partnercom_id, company.name_prt)}
                />
              ))}
            </section>
          )}
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={selectedData?.name}
        loading={removeLoading}
      />
    </div>
  );
}
